import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.TextButton
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import models.DomainGame
import models.GameRepository
import models.Player
import org.jetbrains.compose.resources.stringResource
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.*

private sealed interface LobbyCell
private data class PlayerCell(val player: Player) : LobbyCell
private object InviteCell : LobbyCell
private object AddPlayerCell : LobbyCell
private object AddBotCell : LobbyCell

@Composable
internal fun PlayersGrid(
    game: DomainGame?,
    playerName: String,
    gameState: GameScreenState,
    uiState: BankoGameScreenUiState
) {
    // Two equal-width columns that wrap to as many rows as needed, so every player is visible at
    // once (no internal scroll) and all cards are the same width. Before the game starts, the host
    // sees "Add Player" / "Add Bot" tiles inline in the grid, so the roster reads as a lineup
    // filling up — adding is part of the grid, not a separate form. A lone trailing cell keeps its
    // half width via a spacer rather than stretching full-width.
    var showAddPlayerDialog by remember { mutableStateOf(false) }

    val cells: List<LobbyCell> = buildList {
        (game?.players ?: emptyList()).forEach { add(PlayerCell(it)) }
        if (!gameState.isStarted) {
            // Invite leads (real friends are the best way to fill the lineup) and shows for guests
            // too — anyone with the code can bring more people in. Bots next (the "make it
            // startable now" action), host-created players last since pass-and-play is the
            // nichest way to add someone.
            if (uiState.gameCode != GameRepository.LOCAL_GAME_CODE) {
                add(InviteCell)
            }
            if (gameState.isHost) {
                add(AddBotCell)
                add(AddPlayerCell)
            }
        }
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        cells.chunked(2).forEach { rowCells ->
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                rowCells.forEach { cell ->
                    when (cell) {
                        is PlayerCell -> PlayerRow(
                            modifier = Modifier.weight(1f),
                            playerName = cell.player.name,
                            playerScore = cell.player.points.toString(),
                            controllerIsHost = gameState.hostPlayer?.name == playerName,
                            isHostCreated = cell.player.hostCreated,
                            rowIsHost = gameState.hostPlayer?.name == cell.player.name,
                            gameIsStarted = gameState.isStarted,
                            isCurrentTurn = !gameState.isGameOver &&
                                    gameState.isStarted &&
                                    game?.currentPlayer == cell.player.name,
                            isActive = !gameState.isGameOver && isPlayerActive(game, cell.player.name),
                            makeHost = { uiState.makeHost(cell.player.name) },
                            removePlayer = { uiState.removePlayer(cell.player.name) },
                        )

                        InviteCell -> {
                            val shareInvite = rememberInviteSharer()
                            val inviteMessage = stringResource(
                                Res.string.invite_share_message,
                                uiState.gameCode,
                                InviteLinks.buildInviteUrl(uiState.gameCode),
                            )
                            InviteSlotTile(
                                modifier = Modifier.weight(1f),
                                onClick = {
                                    Analytics.inviteShared(
                                        humanCount = game?.players?.count { !it.isBot } ?: 0,
                                        botCount = game?.players?.count { it.isBot } ?: 0,
                                    )
                                    shareInvite(inviteMessage)
                                },
                            )
                        }

                        AddPlayerCell -> AddSlotTile(
                            modifier = Modifier.weight(1f),
                            label = stringResource(Res.string.add_player),
                            icon = BankoIcons.PersonAdd,
                            onClick = { showAddPlayerDialog = true },
                        )

                        AddBotCell -> AddSlotTile(
                            modifier = Modifier.weight(1f),
                            label = stringResource(Res.string.add_bot),
                            icon = BankoIcons.SmartToy,
                            onClick = { uiState.addBot() },
                        )
                    }
                }
                if (rowCells.size == 1) {
                    Spacer(modifier = Modifier.weight(1f))
                }
            }
        }
    }

    if (showAddPlayerDialog) {
        AddPlayerDialog(
            game = game,
            uiState = uiState,
            onDismiss = {
                uiState.updateNewPlayerNameInput("")
                showAddPlayerDialog = false
            },
        )
    }
}

/**
 * The lobby's hero action: share the invite link. Deliberately the one solid, accent-filled tile
 * among the dashed [AddSlotTile]s — getting real friends in is the primary way to fill the lineup,
 * so it must not read as just another gray option. Same metrics as the add tiles.
 */
@Composable
private fun InviteSlotTile(
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    val shape = RoundedCornerShape(14.dp)
    val content = MaterialTheme.colorScheme.onPrimaryContainer
    Box(
        modifier = modifier
            .fillMaxWidth()
            .clip(shape)
            .background(MaterialTheme.colorScheme.primaryContainer)
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(CircleShape)
                    .background(content.copy(alpha = 0.12f)),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    imageVector = BankoIcons.Share,
                    contentDescription = null,
                    tint = content,
                    modifier = Modifier.size(20.dp),
                )
            }
            Spacer(modifier = Modifier.width(10.dp))
            Text(
                text = stringResource(Res.string.invite_friends),
                color = content,
                fontSize = 15.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

/**
 * An empty "slot" in the player grid that adds a participant. Dashed outline + a soft icon badge so
 * it reads as an invitation to fill the lineup, visually subordinate to the filled player cards.
 * Matches a player card's height (36dp badge + the same row padding).
 */
@Composable
private fun AddSlotTile(
    label: String,
    icon: ImageVector,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    val accent = MaterialTheme.colorScheme.outline
    val content = MaterialTheme.colorScheme.onSurfaceVariant
    val shape = RoundedCornerShape(14.dp)
    Box(
        modifier = modifier
            .fillMaxWidth()
            .clip(shape)
            .clickable(onClick = onClick)
            .drawBehind {
                drawRoundRect(
                    color = accent,
                    style = Stroke(
                        width = 1.5.dp.toPx(),
                        pathEffect = PathEffect.dashPathEffect(floatArrayOf(12f, 9f), 0f),
                    ),
                    cornerRadius = CornerRadius(14.dp.toPx()),
                )
            },
        contentAlignment = Alignment.Center,
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(CircleShape)
                    .background(content.copy(alpha = 0.12f)),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = content,
                    modifier = Modifier.size(20.dp),
                )
            }
            Spacer(modifier = Modifier.width(10.dp))
            Text(
                text = label,
                color = content,
                fontSize = 15.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

/**
 * Name entry for adding a local player. Stays open after each add (clearing the field) so the host
 * can add several players in a row; "Done" closes it.
 */
@Composable
private fun AddPlayerDialog(
    game: DomainGame?,
    uiState: BankoGameScreenUiState,
    onDismiss: () -> Unit,
) {
    val newPlayerNameInput by uiState.newPlayerNameInputFlow.collectAsState()
    val nameExists = game?.players?.any { it.name == newPlayerNameInput } == true
    val canAdd = newPlayerNameInput.isNotBlank() && !nameExists

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(text = stringResource(Res.string.add_player)) },
        text = {
            Column {
                OutlinedTextField(
                    value = newPlayerNameInput,
                    onValueChange = { uiState.updateNewPlayerNameInput(it) },
                    singleLine = true,
                    isError = nameExists,
                    shape = RoundedCornerShape(14.dp),
                    placeholder = { Text(text = stringResource(Res.string.new_player_name)) },
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                    keyboardActions = KeyboardActions(onDone = { if (canAdd) uiState.addLocalPlayer() }),
                )
                if (nameExists) {
                    Spacer(modifier = Modifier.height(6.dp))
                    Text(
                        text = stringResource(Res.string.player_already_exists),
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.labelMedium,
                    )
                }
            }
        },
        confirmButton = {
            TextButton(enabled = canAdd, onClick = { uiState.addLocalPlayer() }) {
                Text(text = stringResource(Res.string.add_player))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(text = "Done")
            }
        },
    )
}

@Composable
fun PlayerRow(
    playerName: String,
    playerScore: String,
    isHostCreated: Boolean,
    rowIsHost: Boolean,
    controllerIsHost: Boolean,
    gameIsStarted: Boolean,
    isCurrentTurn: Boolean,
    isActive: Boolean,
    modifier: Modifier = Modifier,
    makeHost: () -> Unit = { },
    removePlayer: () -> Unit = { },
) {
    // Emphasis ladder: the player whose turn it is (solid primary) > players still in the round
    // (primary container) > players who've banked out this round (muted surface).
    val container = when {
        isCurrentTurn -> MaterialTheme.colorScheme.primary
        isActive -> MaterialTheme.colorScheme.primaryContainer
        else -> MaterialTheme.colorScheme.surfaceVariant
    }
    val onContainer = when {
        isCurrentTurn -> MaterialTheme.colorScheme.onPrimary
        isActive -> MaterialTheme.colorScheme.onPrimaryContainer
        else -> MaterialTheme.colorScheme.onSurfaceVariant
    }

    Surface(
        shape = RoundedCornerShape(14.dp),
        color = container,
        // Lift every card off the flat background; the active player's card lifts further with a
        // tertiary ring, resting cards get a hairline outline so they still read as distinct cards.
        border = if (isCurrentTurn) {
            BorderStroke(2.dp, MaterialTheme.colorScheme.tertiary)
        } else {
            BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
        },
        shadowElevation = if (isCurrentTurn) 6.dp else 2.dp,
        modifier = modifier.fillMaxWidth(),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(CircleShape)
                    .background(onContainer.copy(alpha = 0.18f)),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = playerName.firstOrNull()?.uppercase() ?: "?",
                    color = onContainer,
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp,
                )
            }
            Spacer(modifier = Modifier.width(10.dp))
            Column(modifier = Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = playerName,
                        color = onContainer,
                        fontSize = 16.sp,
                        fontWeight = if (isCurrentTurn) FontWeight.Bold else FontWeight.SemiBold,
                        overflow = TextOverflow.Ellipsis,
                        maxLines = 1,
                        modifier = Modifier.weight(1f, fill = false),
                    )
                    if (rowIsHost) {
                        Spacer(modifier = Modifier.width(4.dp))
                        Icon(
                            imageVector = BankoIcons.Star,
                            contentDescription = "Host",
                            tint = Color(0xFFFFC107),
                            modifier = Modifier.size(16.dp),
                        )
                    }
                }
                if (gameIsStarted) {
                    Text(
                        text = "$playerScore pts",
                        color = onContainer.copy(alpha = 0.85f),
                        fontSize = 13.sp,
                    )
                }
            }
            if (controllerIsHost && !rowIsHost) {
                var showPlayerMenu by remember { mutableStateOf(false) }
                Box {
                    IconButton(onClick = { showPlayerMenu = true }, modifier = Modifier.size(28.dp)) {
                        Icon(
                            imageVector = BankoIcons.MoreVert,
                            contentDescription = "Player options",
                            tint = onContainer,
                        )
                    }
                    DropdownMenu(
                        expanded = showPlayerMenu,
                        onDismissRequest = { showPlayerMenu = false }
                    ) {
                        DropdownMenuItem(text = {
                            Text(text = stringResource(Res.string.remove_player))
                        }, onClick = {
                            removePlayer()
                            showPlayerMenu = false
                        })
                        if (!isHostCreated) {
                            DropdownMenuItem(text = {
                                Text(text = stringResource(Res.string.make_host))
                            }, onClick = {
                                makeHost()
                                showPlayerMenu = false
                            })
                        }
                    }
                }
            }
        }
    }
}
